import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "@/App";
import AppProviders from "@/app/AppProviders";
import { queryClient } from "@/lib/react-query";
import { debugLogger } from '@/utils/debug';

const RECOVERY_KEYS = [
  'vite:preload-recovery',
  'vite:script-recovery',
  'vite:modulepreload-recovery',
  'vite:rejection-recovery',
];

function clearRecoveryKeys() {
  if (typeof window === 'undefined') {
    return;
  }

  for (const key of RECOVERY_KEYS) {
    window.sessionStorage.removeItem(key);
  }
}

function registerInvalidateQueryListener() {
  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener('invalidate-query', ((event: CustomEvent) => {
    const { queryKey } = event.detail;
    if (queryKey) {
      queryClient.invalidateQueries({ queryKey });
      debugLogger.log('Invalidated query:', queryKey);
    }
  }) as EventListener);
}

interface MountClientAppOptions {
  hydrate?: boolean;
}

export function mountClientApp({ hydrate = false }: MountClientAppOptions = {}) {
  clearRecoveryKeys();
  registerInvalidateQueryListener();
  const rootElement = document.getElementById('root');

  if (!rootElement) {
    throw new Error('Missing root element');
  }

  const app = (
    <React.StrictMode>
      <AppProviders>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </AppProviders>
    </React.StrictMode>
  );

  if (hydrate) {
    ReactDOM.hydrateRoot(rootElement, app);
    return;
  }

  ReactDOM.createRoot(rootElement).render(app);
}